import { adjustedLevel, levelOf, type Mode, type SectorSnapshot } from "@trip/domain/danger";
import { X } from "lucide-react";
import type { CSSProperties } from "react";
import { alcaldiaName, LEVEL_COLORS, zoneBreakdown } from "../lib/danger-sectors.ts";
import { hourLabel, LEVEL_WORDS, MODE_WORDS, shortCredit } from "./DangerLayer.tsx";

/**
 * What makes a zone what it is: its level now, for this mode and hour, the level and score it has
 * before the hour is taken into account, and each crime the mode counts with how much it adds to
 * that score, worst first. In the place view's frame, over the bottom of the map.
 */
export function ZonePanel({
  sector,
  mode,
  hour,
  attribution,
  period = null,
  onClose,
}: {
  sector: SectorSnapshot;
  mode: Mode;
  hour: number;
  /**
   * The snapshot's credit, which its licence asks for wherever its numbers are shown: said as the
   * compass says it, in one short line.
   */
  attribution: string;
  /** The months the counts cover ("Oct 2025 – Sep 2026"), as the snapshot gives them. */
  period?: string | null;
  onClose: () => void;
}) {
  const title = `${sector.sector} · ${alcaldiaName(sector.alcaldia)}`;
  const score = mode === "FOOT" ? sector.foot.score : sector.vehicle.score;
  const level = adjustedLevel(score, hour, mode);
  const rows = zoneBreakdown(sector, mode);
  return (
    <section className="poi-editor poi-view zone-panel" aria-label={title}>
      <header className="poi-editor-head">
        <h2 className="poi-editor-title">{title}</h2>
        <button
          type="button"
          className="icon-button poi-editor-close"
          aria-label="Close"
          onClick={onClose}
        >
          <X size={20} strokeWidth={1.75} aria-hidden="true" />
        </button>
      </header>
      <div className="poi-editor-body poi-view-body">
        <div className="zone-total">
          <span
            className="poi-badge"
            style={{ "--badge": LEVEL_COLORS[level - 1] } as CSSProperties}
          >{`${level} of 6 · ${LEVEL_WORDS[level - 1]}`}</span>
          <span className="small">{`${MODE_WORDS[mode]} · adjusted for ${hourLabel(hour)}`}</span>
        </div>
        <p className="small muted">{`Base level ${levelOf(score)} of 6 · score ${score.toFixed(1)}`}</p>
        <h3 className="label">{period ? `Reported ${period}` : "Reported"}</h3>
        {rows.length === 0 ? (
          <p className="small muted">None of the crimes counted here were reported</p>
        ) : (
          <ul className="plain zone-crimes" aria-label="Crimes">
            {rows.map((r) => {
              const share = score > 0 ? (r.contribution / score) * 100 : 0;
              return (
                <li key={r.crime} className="zone-crime">
                  <span className="small zone-crime-name">{r.label}</span>
                  <span className="small muted zone-crime-figures">
                    {`${r.count} reported · ×${r.weight} · ${Math.round(share)}% of the score`}
                  </span>
                  <span className="zone-crime-bar" aria-hidden="true">
                    <span style={{ width: `${Math.min(100, share)}%` }} />
                  </span>
                </li>
              );
            })}
          </ul>
        )}
        <p className="small muted">{shortCredit(attribution)}</p>
      </div>
    </section>
  );
}
